// AGC: Modal for picking a custom machine icon
// AGC: Auto-saves on tap. Also updates machine subtype when icon subtype changes.

import React, { useState, useRef, useEffect } from 'react'
import { X, Check, Save } from 'lucide-react'
import { ICON_CATALOG } from '../../types/machineryIcons'

export interface IconSelection {
  iconPath: string
  category: string
  subtypeKey: string
  subtypeLabel: string
}

interface MachineIconPickerProps {
  isOpen: boolean
  currentIconPath: string
  initialCategory: string
  onSelect: (selection: IconSelection) => void
  onClose: () => void
}

const CAT_NORMALIZE: Record<string, string> = {
  tractor: 'trator', harvester: 'colheitadeira', sprayer: 'pulverizador',
  planter: 'plantadeira', spreader: 'distribuidor', implement: 'implemento',
  truck: 'transporte', drone: 'outros',
}

export const MachineIconPicker: React.FC<MachineIconPickerProps> = ({
  isOpen,
  currentIconPath,
  initialCategory,
  onSelect,
  onClose,
}) => {
  const normalizedCat = CAT_NORMALIZE[initialCategory?.toLowerCase()] || initialCategory?.toLowerCase() || 'trator'
  const [activeTab, setActiveTab] = useState(normalizedCat)
  const [lastSavedPath, setLastSavedPath] = useState(currentIconPath)
  const tabsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      setActiveTab(normalizedCat)
      setLastSavedPath(currentIconPath)
    }
  }, [isOpen, normalizedCat, currentIconPath])

  useEffect(() => {
    if (!isOpen || !tabsRef.current) return
    const activeEl = tabsRef.current.querySelector('[data-active="true"]')
    if (activeEl) activeEl.scrollIntoView({ inline: 'center', block: 'nearest' })
  }, [isOpen, activeTab])

  if (!isOpen) return null

  const activeCatalog = ICON_CATALOG.find(c => c.id === activeTab)

  const handleSelect = (path: string, category: string, subtypeKey: string, subtypeLabel: string) => {
    setLastSavedPath(path)
    onSelect({ iconPath: path, category, subtypeKey, subtypeLabel })
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="flex-1 min-h-[40px]" />
      <div
        className="bg-white dark:bg-gray-900 rounded-t-3xl flex flex-col max-h-[88vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* AGC: Header with Salvar */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Escolher Ícone</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-full text-sm font-semibold active:bg-emerald-700"
            >
              <Save className="w-3.5 h-3.5" />
              Salvar
            </button>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* AGC: Category tabs */}
        <div ref={tabsRef} className="flex gap-2 px-4 pb-3 overflow-x-auto flex-shrink-0 scrollbar-hide">
          {ICON_CATALOG.map(cat => (
            <button
              key={cat.id}
              data-active={activeTab === cat.id}
              onClick={() => setActiveTab(cat.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap flex-shrink-0 transition-colors ${
                activeTab === cat.id
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* AGC: Subtypes + color grids */}
        <div className="flex-1 overflow-y-auto px-4 pb-24 space-y-5">
          {activeCatalog?.subtypes.map(sub => (
            <div key={sub.key}>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{sub.label}</p>
              <div className="grid grid-cols-4 gap-2">
                {sub.colors.map(variant => {
                  const isSelected = lastSavedPath === variant.path
                  return (
                    <button
                      key={variant.path}
                      onClick={() => handleSelect(variant.path, activeCatalog.id, sub.key, sub.label)}
                      className={`relative aspect-square rounded-xl border-2 p-1 transition-all ${
                        isSelected
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 shadow-md'
                          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={variant.path}
                        alt={`${sub.label} ${variant.color}`}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                      {isSelected && (
                        <div className="absolute top-0.5 right-0.5 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <span className="absolute bottom-0 left-0 right-0 text-[9px] text-center text-gray-400 capitalize">
                        {variant.color.replace(/\d+$/, '')}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default MachineIconPicker
